import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';

import '../contracts/SiteTemplates_ListaResponse';
import '../contracts/SiteTemplates_GetResponse';
import '../contracts/SiteTemplates_SalvarInput';
import '../contracts/SiteTemplates_SalvarResponse';
import type { SiteTemplates_SalvarInput } from '../contracts/SiteTemplates_SalvarInput';
import type { SiteTemplates_TemplateRow } from '../contracts/SiteTemplates_TemplateRow';

/**
 * Composable for the "Templates de e-mail" staff panel (institutional website
 * form notifications, Full-only). Mirrors useSiteUnidades.ts.
 *
 * The address lists travel as RAW comma-separated text in both directions —
 * the panel edits them in a textarea and posts back what was typed. Splitting
 * them here would only invent ways to lose it; the site gets them split by the
 * backend (Site_TemplateRow), and the two shapes are deliberate.
 */
export function useSiteTemplates() {
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Fetch every template (staff view, inactive included). Unpaged: there is
   * one row per form and a tenant has a handful of forms.
   */
  const fetchTemplates = async (): Promise<SiteTemplates_TemplateRow[]> => {
    log('useSiteTemplates', 'fetchTemplates', 'Fetching all templates');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get('/index.php/config/site_templates/lista', { responseContract: 'SiteTemplates_ListaResponse' });
      log('useSiteTemplates', 'fetchTemplates', 'Response', response);

      if (response.success) {
        return (response.dados?.items || []) as SiteTemplates_TemplateRow[];
      } else {
        throw new Error(response.error || 'Erro ao buscar templates');
      }
    } catch (err) {
      logError('useSiteTemplates', 'fetchTemplates', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch a single template by ID — the list already carries every field, but
   * the form reloads through this endpoint so an edit always starts from what
   * the database holds right now.
   */
  const fetchTemplate = async (templateId: number): Promise<SiteTemplates_TemplateRow | undefined> => {
    log('useSiteTemplates', 'fetchTemplate', 'Fetching template', { templateId, type: typeof templateId });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/config/site_templates/get/${templateId}`, { responseContract: 'SiteTemplates_GetResponse' });
      log('useSiteTemplates', 'fetchTemplate', 'Response', response);

      if (response.success) {
        return response.dados?.template as SiteTemplates_TemplateRow | undefined;
      } else {
        const errorMsg = response.error || 'Erro ao buscar template';
        logError('useSiteTemplates', 'fetchTemplate', 'Backend returned error', { response, error: errorMsg });
        throw new Error(errorMsg);
      }
    } catch (err) {
      logError('useSiteTemplates', 'fetchTemplate', 'Error', {
        templateId,
        message: err.message,
        status: err.status,
        statusText: err.statusText,
        data: err.data,
        response: err.response
      });
      error.value = err.message;

      if ((err as any).data) {
        const enhancedError = new Error((err as any).data.error || (err as any).data.Error || err.message);
        (enhancedError as any).originalError = err;
        (enhancedError as any).response = (err as any).response;
        (enhancedError as any).data = (err as any).data;
        throw enhancedError;
      }

      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Create or update a template (`id` present in the payload = update).
   */
  const salvarTemplate = async (data: SiteTemplates_SalvarInput) => {
    log('useSiteTemplates', 'salvarTemplate', 'Saving template', data);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post('/index.php/config/site_templates/salvar', data, {
        responseContract: 'SiteTemplates_SalvarResponse',
        inputContract: 'SiteTemplates_SalvarInput'
      });
      log('useSiteTemplates', 'salvarTemplate', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao salvar template');
      }
    } catch (err) {
      logError('useSiteTemplates', 'salvarTemplate', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    isLoading,
    error,

    // CRUD operations (the backend offers no delete — a form is turned off
    // with `ativo`, which keeps the text that was written)
    fetchTemplates,
    fetchTemplate,
    salvarTemplate
  };
}
